import { Box, CircularProgress, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import VnModalBody from '@/modals/components/VnModalBody';
import { useModalContext } from '@/modals/VNModalProvider';
import type { LiveProgram } from '@/models/channel';
import type { Input } from '@/models/input';
import type { LiveBroadcast } from '@/models/LiveBroadcast';
import { FuelEntityType, FuelPortalService } from '@/services/fuel-portal/fuel-portal-service';
import { applicationDataSelector } from '@/store/slices/application-data';

import { useObservedInputLiveStatus } from '../InputSource/InputSourceLiveStatusProvider';
import InputSourcePreviewPanel from '../InputSource/InputSourcePreviewPanel';

type LoadState = 'loading' | 'ready' | 'error';

interface LiveBroadcastsStartModalProps {
  broadcast: LiveBroadcast;
  preloadedInput?: Input | null;
}

const LiveBroadcastsStartModalContent = ({
  broadcast,
  input,
  livePrograms,
}: {
  broadcast: LiveBroadcast;
  input: Input;
  livePrograms: LiveProgram[];
}) => {
  const { setPreventClose, updateTitle } = useModalContext();
  const liveStatus = useObservedInputLiveStatus(input.id);
  const isManualActive = Boolean(liveStatus?.liveProgramIds?.length);

  useEffect(() => {
    if (input.name) {
      updateTitle(input.name);
    }
  }, [input.name, updateTitle]);

  useEffect(() => {
    if (isManualActive) {
      setPreventClose(true, {
        title: 'Existing Live Preview',
        message: 'The manual start will continue until you select manual stop.',
        confirmText: 'OK',
      });
    } else {
      setPreventClose(false);
    }

    return () => setPreventClose(false);
  }, [isManualActive, setPreventClose]);

  return (
    <InputSourcePreviewPanel
      input={input}
      livePrograms={livePrograms}
      defaultBroadcastId={broadcast.id}
      liveStatus={liveStatus}
    />
  );
};

const LiveBroadcastsStartModal = ({ broadcast, preloadedInput }: LiveBroadcastsStartModalProps) => {
  const { ouData } = useSelector(applicationDataSelector);

  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [input, setInput] = useState<Input | null>(null);

  const livePrograms: LiveProgram[] = broadcast.id ? [{ id: broadcast.id, name: broadcast.title } as LiveProgram] : [];

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!broadcast.inputSourceId || !ouData?.ouId) {
        if (!cancelled) {
          setLoadState('error');
        }
        return;
      }

      setLoadState('loading');

      try {
        let resolvedInput = preloadedInput ?? null;

        if (!resolvedInput?.id) {
          const fetched = await FuelPortalService.getEntityById<Input>(FuelEntityType.Input, broadcast.inputSourceId, {
            fullInfo: true,
          });

          if (!fetched?.id) {
            if (!cancelled) {
              setLoadState('error');
            }
            return;
          }

          if (fetched.entityOuId && fetched.entityOuId !== ouData.ouId) {
            if (!cancelled) {
              setLoadState('error');
            }
            return;
          }

          resolvedInput = fetched;
        }

        if (cancelled) {
          return;
        }

        setInput(resolvedInput);

        if (!cancelled) {
          setLoadState('ready');
        }
      } catch {
        if (!cancelled) {
          setLoadState('error');
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [broadcast.inputSourceId, ouData?.ouId, preloadedInput]);

  if (loadState === 'loading') {
    return (
      <VnModalBody>
        <Box className="live-broadcasts-start-modal__loading">
          <CircularProgress />
        </Box>
      </VnModalBody>
    );
  }

  if (loadState === 'error' || !input) {
    return (
      <VnModalBody>
        <Typography variant="body2" color="text.secondary">
          Unable to load input source for this broadcast.
        </Typography>
      </VnModalBody>
    );
  }

  return (
    <VnModalBody>
      <LiveBroadcastsStartModalContent broadcast={broadcast} input={input} livePrograms={livePrograms} />
    </VnModalBody>
  );
};

export default LiveBroadcastsStartModal;
